import {gameDataReload, serverInfoReload} from "../../features/serverInfo/useServerInfo";
import {CloudSyncOutlined} from "@ant-design/icons";
import React from "react";

export const ROLE = {
    NEWBIE: 0,  // 최초 가입자
    SERVICE: 1,  // 일부 기능만 조회 가능 (유저 정보, CS)
    SELECT: 2, // 모든 기능 조회 가능
    DEVELOP: 3, // 조회 및 입력 가능
    ADMIN: 4,   // 관리자
    SUPER: 5,   // 슈퍼 관리자
}

export const DIALOG_KEY = {
    OPEN: 'open',
    TITLE: 'title',
    BODY: 'body',
    CONFIRM_TEXT: 'confirmText',
    HANDLE: 'handle',
    BUTTON_TEXT: 'buttonText',
    USE_INPUT: 'useInput'
}

export const ENV = {
    DEVELOP: 'develop',
    QA: 'qa',
    PRODUCTION: 'production',
    DOCKER: 'docker'
}

export const IS_DOCKER = process.env.REACT_APP_ENV === 'docker';

export const SERVER_CONFIG = {
    [ENV.DEVELOP]:    { label: 'Develop', fullLabel: 'Develop (개발서버)',  color: '#52c41a', icon: '🔧',
        theme: { accent: '#93C7AE', accent2: '#77B399', accentHover: '#84BCA1', accent2Hover: '#68A78C', ink: '#2E7D63', inkRgb: '46, 125, 99', onAccent: '#123F30' } },
    [ENV.QA]:         { label: 'QA',      fullLabel: 'QA (빌드/QA 서버)',   color: '#faad14', icon: '⚙️',
        theme: { accent: '#B7A6D9', accent2: '#9C87C9', accentHover: '#A995CF', accent2Hover: '#8D77BE', ink: '#63459E', inkRgb: '99, 69, 158', onAccent: '#33235A' } },
    [ENV.PRODUCTION]: { label: 'LIVE',    fullLabel: 'LIVE (라이브 서버)',  color: '#ff4d4f', icon: '🚀',
        theme: { accent: '#E3A7B7', accent2: '#D68DA1', accentHover: '#D998AA', accent2Hover: '#C97E93', ink: '#A8455F', inkRgb: '168, 69, 95', onAccent: '#5E1F33' } },
    [ENV.DOCKER]:     { label: 'Docker',  fullLabel: 'Docker (로컬)',        color: '#1677ff', icon: '🐳',
        theme: { accent: '#A9BDDD', accent2: '#8FA6CE', accentHover: '#9AAFD3', accent2Hover: '#8098C3', ink: '#42619F', inkRgb: '66, 97, 159', onAccent: '#1D3059' } },
};

// 현재 서버(env)의 테마 색을 CSS 변수로 문서 루트에 적용.
// AntD 모달/드롭다운은 body로 portal되므로 :root에 심어야 함께 물든다.
export const applyEnvTheme = (env) => {
    const t = (SERVER_CONFIG[env] || SERVER_CONFIG[ENV.DEVELOP]).theme;
    const root = document.documentElement.style;
    root.setProperty('--env-accent', t.accent);          // 서피스(그라데이션/버튼)
    root.setProperty('--env-accent-2', t.accent2);
    root.setProperty('--env-accent-hover', t.accentHover);
    root.setProperty('--env-accent-2-hover', t.accent2Hover);
    root.setProperty('--env-ink', t.ink);                // 흰 배경 위 텍스트/보더
    root.setProperty('--env-accent-rgb', t.inkRgb);      // 저알파 틴트(배경 물들이기)
    root.setProperty('--env-on-accent', t.onAccent);     // 파스텔 서피스 위 텍스트
};

// allServers: true → docker 환경에서도 Develop/QA/Live 전부 반환 (PlayerCopy source용)
// includeProduction: false → Production 제외 (Login용)
export const getServerOptions = ({ allServers = false, includeProduction = true } = {}) => {
    if (IS_DOCKER && !allServers) return [ENV.DOCKER];
    const list = [ENV.DEVELOP, ENV.QA];
    if (includeProduction) list.push(ENV.PRODUCTION);
    return list;
};

// EventEntity
export const eventList = [
    {
        value: 'BINGO',
        label: '빙고 이벤트',
        idx: 10101
    },
    {
        value: 'POINT',
        label: '재화 획득 이벤트',
        idx: 10201,
    },
    {
        value: 'HAMMER',
        label: '구데타마 찾기 이벤트',
        idx: 10301
    },
    {
        value: 'PUDDING',
        label: '폼폼푸린의 푸딩 대회',
        idx: 10401
    },
    {
        value: 'ICECREAM',
        label: '포차코와 아이스크림',
        idx: 10501
    },
    {
        value: 'SHOPPINGTOWN',
        label: '쇼핑타운 이벤트',
        idx: 10601
    },
    {
        value: 'FISHING',
        label: '낚시 이벤트',
        idx: 10701
    },
    {
        value: 'JEWELRY',
        label: '쥬얼리 이벤트',
        idx: 10801
    }
];

export const onOffList = [
    {
        value: 1,
        label: 'On'
    },
    {
        value: 0,
        label: 'Off'
    },
];

export const operationTypeList = [
    {
        value: 1,
        label: '기간 내',
        color: 'blue'
    },
    {
        value: 2,
        label: '월 단위',
        color: 'magenta'
    },
    {
        value: 3,
        label: '주 단위',
        color: 'red'
    },
    {
        value: 4,
        label: '일 단위',
        color: 'orange'
    },
    {
        value: 5,
        label: '선행 조건 발동',
        color: 'purple'
    }
];

// EventEntity - 선행 조건(트리거). 실제 정의는 triggerEnum.js 에 있다 (의존성 없이 재사용하기 위함)
export {
    OPERATION_TYPE_TRIGGER,
    triggerTypeList,
    triggerOpList,
    triggerActionList,
    WEEKDAY_ALL,
    WEEKDAY_WEEKDAYS,
    WEEKDAY_WEEKEND,
    weekdayPresetList
} from './triggerEnum';

// BannerEntity
export const actionList = [
    {
        value: 0,
        label: 'WebView'
    },
    {
        value: 1,
        label: 'openURL'
    },
    {
        value: 2,
        label: 'UI'
    },
];

// CouponEntity
export const couponTypeList = [
    {
        value: 0,
        label: '공용 쿠폰'
    },
    {
        value: 1,
        label: '개별 쿠폰'
    },
];

// CS
export const csTypeList = [
    {
        value: 0,
        label: 'Account',
        desc: '계정 문의'
    },
    {
        value: 1,
        label: 'Bug',
        desc: '버그 문의'
    },
    {
        value: 2,
        label: 'Payment',
        desc: '결제 문의'
    },
    {
        value: 3,
        label: 'ETC',
        desc: '기타 문의'
    },
    {
        value: -1,
        label: 'ALL'
    },
];
export const csStatList = [
    {
        value: -1,
        label: 'ALL'
    },
    {
        value: 0,
        label: 'Wait'
    },
    {
        value: 1,
        label: 'Complete'
    }
];
export const csEtcList = [
    {
        value: 0,
        label: 'None'
    },
    {
        value: 1,
        label: 'UserKey'
    },
    {
        value: 2,
        label: 'Pid'
    },
    {
        value: 3,
        label: 'NickName'
    },
    {
        value: 4,
        label: 'No'
    }
];

// MessageEntity
export const messageTypeList = [
    {
        value: 0,
        label: '즉시 발송'
    },
    {
        value: 1,
        label: '예약 발송'
    },
    {
        value: 2,
        label: '반복 발송'
    }
];

export const cycleTypeList = [
    {
        value: 0,
        label: '없음'
    },
    {
        value: 1,
        label: '매일'
    },
    {
        value: 2,
        label: '매주'
    },
    {
        value: 3,
        label: '매월'
    }
];

export const weekList = [
    {
        value: 0,
        label: '일'
    },
    {
        value: 1,
        label: '월'
    },
    {
        value: 2,
        label: '화'
    },
    {
        value: 3,
        label: '수'
    },
    {
        value: 4,
        label: '목'
    },
    {
        value: 5,
        label: '금'
    },
    {
        value: 6,
        label: '토'
    }
];

export const MESSAGE_TYPE = {
    IMMEDIATE: 0,
    RESERVATION: 1,
    REPEAT: 2
}

export const CYCLE = {
    MINUTE: 0,
    HOUR: 1,
    DAY: 2,
    WEEK: 3,
    MONTH: 4,
    YEAR: 5,
}

export const WEEK = {
    SUN: 0,
    MON: 1,
    TUE: 2,
    WED: 3,
    THU: 4,
    FRI: 5,
    SAT: 6
}

export const PLATFORM = {
    ALL: 0,
    AOS: 1,
    IOS: 2
}

export const PROCESSING_STAT = {
    PENDING: 0,
    IN_PROGRESS: 1,
    COMPLETE: 2
}

export const validPeriodList = [
    {
        value: 1,
        label: '1일',
    },
    {
        value: 3,
        label: '3일',
    },
    {
        value: 5,
        label: '5일',
    },
    {
        value: 7,
        label: '7일',
    },
    {
        value: 14,
        label: '14일',
    },
    {
        value: 30,
        label: '30일',
    },
    {
        value: 36500,
        label: '영구',
    },
]

export const loginTypeList = ['NONE', 'GUEST', 'FACEBOOK', 'GOOGLE', 'APPLE'];

export const USER_STATE = {
    DEFAULT: 0,
    WITHDRAW: 1,
    SUSPEND: 4,
}

export const userStateList = [
    {
        value: USER_STATE.DEFAULT,
        label: '정상'
    },
    {
        value: USER_STATE.WITHDRAW,
        label: '탈퇴'
    },
    {
        value: USER_STATE.SUSPEND,
        label: '정지'
    }
]

export const onOffValue = [
    {
        color: 'red',
        text: 'Off'
    },
    {
        color: 'blue',
        text: 'On'
    }
]

export const WIDGET_TYPE = {
    NEW_USER: 'newUser',
    DAU: 'dau',
    MAU: 'mau',
    TOTAL: 'total',
    PU: 'pu',
    REVENUE: 'revenue',
    ARPU: 'arpu',
    ARPPU: 'arppu',
}

export const PACKAGE_MANAGER_TYPE = {
    COUNT: 'PackagePurchaseCount',
    AMOUNT: 'PackageSalesAmount',
    RANGE: 'PriceRangePurchaseCount',
    LEVEL: 'UserLevelDistribution',
}

export const ROLE_OPTIONS = [
    {value: ROLE.NEWBIE, label: '신규'},
    {value: ROLE.SERVICE, label: '서비스'},
    {value: ROLE.SELECT, label: '조회전용'},
    {value: ROLE.DEVELOP, label: '개발자'},
    {value: ROLE.ADMIN, label: '관리자'},
    {value: ROLE.SUPER, label: '슈퍼 관리자'}
];

export const PERMISSION = {
    USER_READ: 1,
    USER_WRITE: 2,
    GAME_READ: 3,
    GAME_WRITE: 4,
    CS_READ: 5,
    CS_WRITE: 6,
    SYSTEM_READ: 7,
    SYSTEM_WRITE: 8,
    ETC_ALL_ACCESS: 9,
    SERVER_ACCESS_DEVELOP: 10,
    SERVER_ACCESS_QA: 11,
    SERVER_ACCESS_LIVE: 12,
    COPY_QA: 13,
    COPY_LIVE: 14,
    ANALYTICS_READ: 15,
    USER_STATE_WRITE: 16,
}

export const PERMISSION_OPTIONS = [
    {value: PERMISSION.USER_READ, label: '플레이어 데이터 조회'},
    {value: PERMISSION.USER_WRITE, label: '플레이어 데이터 쓰기'},
    {value: PERMISSION.USER_STATE_WRITE, label: '플레이어 상태 변경'},
    {value: PERMISSION.GAME_READ, label: '게임 관리 조회'},
    {value: PERMISSION.GAME_WRITE, label: '게임 관리 쓰기'},
    {value: PERMISSION.CS_READ, label: '고객센터/운영 조회'},
    {value: PERMISSION.CS_WRITE, label: '고객센터/운영 쓰기'},
    {value: PERMISSION.SYSTEM_READ, label: '시스템 관리 조회'},
    {value: PERMISSION.SYSTEM_WRITE, label: '시스템 관리 쓰기'},
    {value: PERMISSION.SERVER_ACCESS_DEVELOP, label: '서버 접근 Develop'},
    {value: PERMISSION.SERVER_ACCESS_QA, label: '서버 접근 QA'},
    {value: PERMISSION.SERVER_ACCESS_LIVE, label: '서버 접근 Live'},
    {value: PERMISSION.COPY_QA, label: 'QA까지 계정 복사'},
    {value: PERMISSION.COPY_LIVE, label: '라이브 계정 복사'},
    {value: PERMISSION.ANALYTICS_READ, label: '통계 조회'},
    {value: PERMISSION.ETC_ALL_ACCESS, label: '기타 전체 접근'},
]

export const FACILITY_MISSION_TYPE = [
    '상태 없음',
    '진행 중',
    '완료',
    '보상 받은 상태',
]

export const PASS_TYPE = {
    MONTHLY: 1,
    WEEKLY: 2,
    SPECIAL: 3,
}

export const PASS_GRADE = {
    VIP: 1,
    UPGRADE_TO_LUXURY: 2,
    LUXURY: 3,
    LAST_CHANCE: 4,
}

export const REFRESH_TYPE_FUNCTION = {
    SERVER_INFO: {
        func: serverInfoReload,
        title: '서버 정보, CSV 갱신',
        icon: <CloudSyncOutlined style={{fontSize: 'x-large'}}/>,
        desc: '서버 정보 및 CSV파일을 갱신합니다. 진행하시겠습니까?'

    },
    GAME_DATA: {
        func: gameDataReload,
        title: '게임 데이터 정보 갱신',
        icon: <CloudSyncOutlined style={{fontSize: 'x-large'}}/>,
        desc: '게임 데이터를 갱신합니다. \n앱이 유휴 상태였던 플레이어는 타이틀로 이동하게 됩니다. \n이 작업을 진행하시겠습니까?'
    }
}

export const productTypeList = [
    {text: '일반 상점 상품', color: 'magenta', value: 1},
    {text: '패키지 상품', color: 'blue', value: 2},
    {text: '체인 패키지', color: 'green', value: 3},
    {text: '에너지 체인 패키지', color: 'purple', value: 4},
    {text: '에너지 미션 패키지', color: 'red', value: 5},
    {text: '픽원 패키지', color: 'yellow', value: 6},
    {text: '연속 패키지', color: 'orange', value: 7},
    {text: '깜짝 패키지', color: 'cyan', value: 8},
    {text: '복귀, 신규 유저 출석 패키지', color: 'gold', value: 9},
    {text: '캐릭터 패키지', color: 'pink', value: 10},
    {text: '저금통', color: 'lime', value: 11},
    {text: 'HUD 패키지', color: 'coral', value: 12},
    {text: '머지별 할인', color: 'aqua', value: 13},
    {text: '7일의 행복!', color: 'khaki', value: 14},
    {text: '1+1 패키지', color: 'tan', value: 15},
];

export const productType = {
    NORMAL_STORE: 1,
    PACKAGE: 2,
    CHAIN_PACKAGE: 3,
    ENERGY_CHAIN_PACKAGE: 4,
    ENERGY_MISSION_PACKAGE: 5,
    PICKONE_PACKAGE: 6,
    CONTINUOUS_PACKAGE: 7,
    SURPRISE_PACKAGE: 8,
    RETURN_NEW_USER_ATTENDANCE_PACKAGE: 9,
    CHARACTER_PACKAGE: 10,
    BANK: 11
}

export const DATA_CONFIG_KEY = {
    SEASON_THEME_MEMO: 'season_theme_data_config',
    MERGE_EVENT_MEMO: 'merge_event_data_config',
}

export const MAIL_TYPE = {
    ADMIN: 0,       // 운영 보상
    COUPON: 1,      // 쿠폰 보상
    PURCHASE: 2,    // 인앱 구매
    EVENT: 3,       // 이벤트 보상
    CHEAT: 4,       // 치트
    ADMIN_AUTO: 6   // 운영 전체 우편
}

export const SHOP_TYPE = {
    DIAMOND: 1,
    PACKAGE: 2,
    PASS: 3,
    MONTHLY: 4,
}

export const PRODUCT_TYPE = {
    ETC: 0, // 기타 상품
    COMMON: 1, // 일반 상품
    PACKAGE: 2, // 패키지 상품
    CHAIN: 3, // 체인 패키지
    ENERGY_CHAIN: 4, // 에너지 체인 상품
    ENERGY: 5,
    PICK_ONE: 6, // 픽원 상품
    CONTINUOUS: 7, // 연속 상품
    SURPRISE_PACKAGE: 8,    // 깜짝 패키지
    RETURN_NEW_USER_ATTENDANCE_PACKAGE: 9, // 신규 유저 출석 패키지
    CHARACTER_PACKAGE: 10, // 캐릭터 패키지
    BANK: 11 // 에너지 저금통
}

export const MERGE_EVENT_TYPE = {
    FINAL_BLOCK_MERGE: 'FinalBlockMerge',
    FIND_KEY: 'FindKey',
    GET_POINT: 'GetPoint',
    BOSS_RAID: 'BossRaid',
}